import { apiClient } from './apiClient';

/**
 * Internal helper for RPC chat calls
 */
async function rpcChat<T>(company: string, action: string, payload: any): Promise<T> {
  const { data } = await apiClient.post(`/api/${company}/rpc/chat/${action}`, payload, {
    withCredentials: true,
  });
  // If backend returns { ok, data }, unwrap it here
  if ((data as any)?.ok && 'data' in data) {
    return (data as any).data as T;
  }
  // If backend returns { ok: true, conversations } or { ok: true, messages }, etc., return the data directly
  if ((data as any)?.ok) {
    // For responses like { ok: true, conversations } or { ok: true, messages }
    const { ok, ...rest } = data as any;
    return rest as T;
  }
  return data as T;
}

/**
 * Mark messages as read
 */
export async function markMessagesRead(company: string, conversationId: string, upTo: string) {
  await rpcChat<{ updated?: number }>(company, 'mark-read', { conversationId, upTo });
}

/**
 * Send a chat message
 */
export async function sendChatMessage(company: string, conversationId: string, text: string) {
  const result = await rpcChat<{ message: any }>(company, 'send-message', {
    conversationId,
    text,
  });
  return result;
}

/**
 * Send AI chat message
 * Migrated to RPC: POST /api/:company/rpc/chat/ai
 */
export async function sendAIChatMessage(company: string, conversationId: string, text: string) {
  const { data } = await apiClient.post(
    `/api/${company}/rpc/chat/ai`,
    { conversationId, text },
    { withCredentials: true }
  );
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Get conversations list
 */
export async function getConversations(company: string) {
  const result = await rpcChat<{ conversations: any[] }>(company, 'list-conversations', {});
  return result?.conversations || [];
}

/**
 * Get messages for a conversation
 */
export async function getMessages(company: string, conversationId: string, limit?: number) {
  const result = await rpcChat<{ messages: any[] }>(company, 'list-messages', {
    conversationId,
    limit,
  });
  return result?.messages || [];
}

/**
 * Get presence status
 */
export async function getPresence(company: string) {
  const result = await rpcChat<{ users: any[] }>(company, 'get-presence', {});
  return result;
}

/**
 * Create a conversation
 */
export async function createConversation(company: string, payload: { type: string; participantIds: string[]; title?: string }) {
  const result = await rpcChat<{ conversation: any }>(company, 'create-conversation', payload);
  return result?.conversation;
}

/**
 * Update conversation participants (add)
 */
export async function updateConversationParticipants(company: string, conversationId: string, userIds: string[]) {
  const result = await rpcChat<{ conversation: any }>(company, 'update-participants', {
    id: conversationId,
    add: userIds,
  });
  return result?.conversation;
}

/**
 * Remove participant from conversation
 */
export async function removeParticipantFromConversation(company: string, conversationId: string, userId: string) {
  const result = await rpcChat<{ conversation: any }>(company, 'update-participants', {
    id: conversationId,
    remove: [userId],
  });
  return result?.conversation;
}

/**
 * Create AI conversation
 * Migrated to RPC: POST /api/:company/rpc/chat/create-ai-conversation
 */
export async function createAIConversation(company: string) {
  const { data } = await apiClient.post(
    `/api/${company}/rpc/chat/create-ai-conversation`,
    {},
    { withCredentials: true }
  );
  if (data?.ok && 'data' in data) {
    return data.data;
  }
  return data;
}

/**
 * Search conversations
 */
export async function searchConversations(company: string, query: string) {
  const result = await rpcChat<{ results: any[] }>(company, 'search', { q: query });
  // Extract conversations from results array
  if (result?.results && Array.isArray(result.results)) {
    return result.results.map((r: any) => r.conversation || r).filter(Boolean);
  }
  return [];
}

/**
 * Bulk delete messages
 */
export async function bulkDeleteMessages(company: string, messageIds: string[], forAll: boolean = false) {
  await rpcChat<{ deleted?: number; hidden?: number }>(company, 'bulk-delete-messages', {
    messageIds,
    forAll,
  });
}

/**
 * Delete a single message
 */
export async function deleteMessage(company: string, messageId: string) {
  await rpcChat<{ mode?: string }>(company, 'delete-message', { messageId, forAll: false });
}

/**
 * Add or update reaction to a message
 */
export async function addMessageReaction(company: string, messageId: string, emoji: string) {
  await rpcChat<{ message: any }>(company, 'add-reaction', { messageId, emoji });
}

/**
 * Remove reaction from a message
 */
export async function removeMessageReaction(company: string, messageId: string) {
  await rpcChat<{ message: any }>(company, 'remove-reaction', { messageId });
}

/**
 * Add participant to conversation
 */
export async function addParticipant(company: string, conversationId: string, userId: string) {
  const result = await rpcChat<{ conversation: any }>(company, 'update-participants', {
    id: conversationId,
    add: [userId],
  });
  return result?.conversation;
}

/**
 * Remove participant from conversation
 */
export async function removeParticipant(company: string, conversationId: string, userId: string) {
  const result = await rpcChat<{ conversation: any }>(company, 'update-participants', {
    id: conversationId,
    remove: [userId],
  });
  return result?.conversation;
}

/**
 * Get unread messages count
 */
async function getUnreadCount(company: string): Promise<{ unreadCount: number }> {
  const result = await rpcChat<{ total: number }>(company, 'get-unread-count', {});
  return { unreadCount: result?.total || 0 };
}

// Export all functions as a default object for convenience
const chatApi = {
  markMessagesRead,
  sendChatMessage,
  sendAIChatMessage,
  getConversations,
  getMessages,
  getPresence,
  createConversation,
  updateConversationParticipants,
  removeParticipantFromConversation,
  removeParticipant,
  createAIConversation,
  searchConversations,
  bulkDeleteMessages,
  deleteMessage,
  addParticipant,
  getUnreadCount,
  addMessageReaction,
  removeMessageReaction,
};

export default chatApi;

